import React from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { buildGatewayAdminRecordUrl } from "../../../services/missingReports";

const STATUS_COPY = {
  matched: {
    color: "success",
    label: "Matches found",
    description: "Gateway found one or more products linked to this barcode.",
  },
  not_found: {
    color: "warning",
    label: "No matches",
    description: "Gateway did not find a product for this barcode.",
  },
  unsupported: {
    color: "error",
    label: "Unsupported barcode",
    description: "This barcode is marked as unsupported (for example a non-Canadian product).",
  },
};

const formatMatchQuality = (value) => {
  if (value === "prefix") return "Partial (prefix)";
  if (value === "exact") return "Exact";
  return value || "Exact";
};

const BarcodeFinderResults = ({ loading, error, result }) => {
  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Looking up barcode…
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!result) {
    return null;
  }

  const statusMeta = STATUS_COPY[result.status] || STATUS_COPY.not_found;

  return (
    <Box
      sx={{
        mt: 1,
        p: 1.5,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        backgroundColor: "#fafafa",
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip size="small" color={statusMeta.color} label={statusMeta.label} />
          {result.normalizedBarcode && result.normalizedBarcode !== result.queryBarcode ? (
            <Typography variant="body2" color="text.secondary">
              Searched as {result.normalizedBarcode}
            </Typography>
          ) : null}
        </Stack>

        <Typography variant="body2">{statusMeta.description}</Typography>

        {result.matches.length ? (
          <Table size="small" sx={{ backgroundColor: "#fff", border: "1px solid #eaeaea" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Retailer</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Match</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.matches.map((match, index) => {
                const productUrl = match.beefProductId
                  ? buildGatewayAdminRecordUrl("beef_product", match.beefProductId)
                  : "";
                const retailerName = match.retailer?.name || "Unknown retailer";

                return (
                  <TableRow key={`${match.barcodeId || match.beefProductId || "match"}-${index}`}>
                    <TableCell>
                      {productUrl ? (
                        <Link href={productUrl} target="_blank" rel="noopener noreferrer">
                          {match.productName}
                        </Link>
                      ) : (
                        match.productName
                      )}
                      {match.barcodeCode ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          Barcode {match.barcodeCode}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{retailerName}</TableCell>
                    <TableCell>{formatMatchQuality(match.matchQuality)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : null}
      </Stack>
    </Box>
  );
};

export default BarcodeFinderResults;
